import { clientLogger } from '@/lib/clientLogger';
/** Native outbound softphone lifecycle. The web dialer has its own manager. */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { apiService } from '@/lib/api';
import type { AudioDevice, AudioDeviceType, VoiceCallEvent, VoiceEventHandler } from '@/lib/twilioVoice';

interface NativeCallOptions {
  to: string; callerId: string; clientState: string; displayName?: string; attemptId?: string;
}
interface NativeEvent {
  callSid?: string; attemptId?: string; unclean?: boolean; userInitiated?: boolean; reason?: string;
  devices?: AudioDevice[]; selected?: AudioDeviceType;
  routeRequestId?: number; routeError?: string;
  callAudioActive?: boolean;
}
interface TelnyxVoicePlugin {
  login(opts: { token: string }): Promise<void>;
  logout(): Promise<void>;
  makeCall(opts: NativeCallOptions): Promise<void>;
  endCall(opts: { callSid?: string; attemptId?: string }): Promise<void>;
  muteCall(opts: { muted: boolean; callSid?: string }): Promise<void>;
  getAudioDevices(): Promise<{ devices: AudioDevice[]; selected: AudioDeviceType; callAudioActive?: boolean }>;
  selectAudioDevice(opts: { type: AudioDeviceType; attemptId?: string; callSid?: string; routeRequestId?: number }): Promise<void>;
  sendDigits(opts: { digits: string }): Promise<void>;
  checkMicrophonePermission(): Promise<{ granted: boolean }>;
  requestMicrophonePermission(): Promise<{ granted: boolean }>;
  addListener(eventName: string, cb: (data: NativeEvent) => void): Promise<unknown>;
}
const CapacitorTelnyxVoice = registerPlugin<TelnyxVoicePlugin>('TelnyxVoice');
type LastCall =
  | { kind: 'direct'; toE164: string; callerIdE164: string; displayName?: string; jobId?: string }
  | { kind: 'masked'; jobId: string; contactId: string; callerIdE164: string; which: 'primary' | 'additional'; displayName?: string };
type Timer = ReturnType<typeof setTimeout>;
interface Attempt {
  id: string;
  params: LastCall;
  orgId: string | null;
  companyId: string | null;
  cancelled: boolean;
  terminal: boolean;
  inviteStarted: boolean;
  answered: boolean;
  recovering: boolean;
  retryPending: boolean;
  retryCount: number;
  callSid?: string;
  watchdog?: Timer;
  redialTimer?: Timer;
  idleTimer?: Timer;
  cleanup?: Promise<boolean>;
  stop?: Promise<void>;
  nativeReady?: boolean;
  requestedAudioRoute?: AudioDeviceType;
  routeRequestId: number;
  sentRouteRequestId: number;
  routeCommand?: Promise<void>;
  audioSnapshotId: number;
}

class TelnyxVoiceManager {
  private static _instance: TelnyxVoiceManager | null = null;
  static get instance(): TelnyxVoiceManager {
    return this._instance ??= new TelnyxVoiceManager();
  }
  private static readonly CONNECT_TIMEOUT_MS = 45_000;
  private static readonly REDIAL_DELAY_MS = 1_500;
  private static readonly REDIAL_ENDED_MS = 10_000;
  private _initialized = false;
  private _listenersRegistered = false;
  private _initInFlight: Promise<void> | null = null;
  private _sessionGeneration = 0;
  private _refreshTimer: Timer | null = null;
  private _listeners: VoiceEventHandler[] = [];
  private _currentState: VoiceCallEvent = { state: 'idle' };
  private _attempt: Attempt | null = null;
  private _attemptSequence = 0;
  // Retain recent terminal identities for duplicate native events, including Android's SID-only events.
  private _attempts = new Map<string, Attempt>();
  private _cleanupBarrier: Promise<unknown> = Promise.resolve();
  private _nativeStopBarrier: Promise<void> = Promise.resolve();
  private _lastCall: LastCall | null = null;
  private _identity = '';
  private _token = '';
  private _tokenExpMs = 0;

  subscribe(handler: VoiceEventHandler): () => void {
    this._listeners.push(handler);
    handler({ ...this._currentState });
    return () => { this._listeners = this._listeners.filter((l) => l !== handler); };
  }

  private _live(a: Attempt | null): a is Attempt {
    return !!a && this._attempt === a && !a.cancelled && !a.terminal;
  }
  private get _callActive(): boolean { return this._live(this._attempt); }
  private _sameContext(a: Attempt): boolean {
    return a.orgId === apiService.getOrgId() && a.companyId === apiService.getCompanyId();
  }
  private _canDial(a: Attempt): boolean {
    if (!this._live(a)) return false;
    if (!this._sameContext(a)) {
      this._complete(a, { reason: 'Your organization or company changed. Please start the call again.' });
      return false;
    }
    return true;
  }

  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform() || this._initialized) return;
    if (this._initInFlight) return this._initInFlight;
    const generation = ++this._sessionGeneration;
    const run = (async () => {
      if (!this._listenersRegistered) {
        await this._registerListeners();
        this._listenersRegistered = true;
      }
      try {
        await this._fetchTokenAndLogin(generation);
      } catch (err) {
        if (generation !== this._sessionGeneration) throw err;
        clientLogger.debug('[TelnyxVoice] login failed; retrying once:', err);
        this._token = '';
        this._tokenExpMs = 0;
        await this._fetchTokenAndLogin(generation);
      }
      if (generation === this._sessionGeneration) this._initialized = true;
    })();
    this._initInFlight = run;
    try { await run; }
    finally { if (this._initInFlight === run) this._initInFlight = null; }
  }

  private _reconnectForNextCall(): void {
    const ended = this._attempt;
    const session = this._sessionGeneration;
    void (async () => {
      await this._nativeStopBarrier;
      await this._cleanupBarrier;
      await this._nativeStopBarrier;
      if (session !== this._sessionGeneration || this._attempt !== ended || this._callActive || ended?.retryPending) return;
      this._initialized = false;
      await this.init();
    })().catch((err) => clientLogger.debug('[TelnyxVoice] next-call login failed:', err));
  }

  async destroy(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const a = this._attempt;
    if (a) { a.cancelled = true; a.retryPending = false; this._clearTimers(a); }
    ++this._sessionGeneration;
    this._initInFlight = null;
    this._initialized = false;
    this._token = '';
    this._tokenExpMs = 0;
    this._clearRefreshTimer();
    if (a?.inviteStarted && !a.terminal) this._complete(a, { unclean: true, userInitiated: true });
    this._attempt = null;
    this._emit({ state: 'idle' });
    // Retain the barrier so a new sign-in cannot race logout's native teardown.
    this._nativeStopBarrier = this._within(CapacitorTelnyxVoice.logout(), 3_000).catch(() => {});
    await this._nativeStopBarrier;
  }

  async makeCall(toE164: string, callerIdE164: string, displayName?: string, jobId?: string): Promise<void> {
    await this._runCall({ kind: 'direct', toE164, callerIdE164, displayName, jobId });
  }
  async makeMaskedCall(jobId: string, contactId: string, callerIdE164: string,
    which: 'primary' | 'additional' = 'primary', displayName?: string): Promise<void> {
    await this._runCall({ kind: 'masked', jobId, contactId, callerIdE164, which, displayName });
  }
  async redial(manual = true): Promise<void> {
    const previous = this._attempt;
    const params = this._lastCall;
    if (!params || this._callActive) return;
    this._initialized = false;
    try { await this._runCall(params, manual ? 0 : (previous?.retryCount ?? 0) + 1); }
    catch (err) { clientLogger.debug('[TelnyxVoice] redial failed:', err); }
  }

  private async _runCall(params: LastCall, retryCount = 0): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    if (this._callActive) throw new Error('A call is already in progress.');
    if (this._attempt) { this._attempt.cancelled = true; this._clearTimers(this._attempt); }
    const a: Attempt = {
      // Correlation only, not an auth secret. Older supported WKWebViews may lack randomUUID.
      id: globalThis.crypto?.randomUUID?.() ?? `attempt-${Date.now()}-${++this._attemptSequence}`,
      params, orgId: apiService.getOrgId(), companyId: apiService.getCompanyId(),
      cancelled: false, terminal: false, inviteStarted: false, answered: false, recovering: false,
      retryPending: false, retryCount, routeRequestId: 0, sentRouteRequestId: 0, audioSnapshotId: 0,
    };
    this._attempt = a;
    this._lastCall = params;
    this._attempts.set(a.id, a);
    if (this._attempts.size > 32) this._attempts.delete(this._attempts.keys().next().value!);
    const to = params.kind === 'direct' ? params.toE164 : `contact:${params.contactId}:${params.which}`;
    this._emit({ state: 'connecting', toE164: to, callerIdE164: params.callerIdE164,
      displayName: params.displayName, isMuted: false, audioRoute: 'earpiece',
      availableAudioDevices: [{ type: 'earpiece', name: 'Phone' }, { type: 'speaker', name: 'Speaker' }] });
    a.watchdog = setTimeout(() => {
      if (!this._live(a)) return;
      clientLogger.debug('[TelnyxVoice] setup watchdog expired', a.id);
      // Invalidate before touching native. A late login must not submit an INVITE.
      this._complete(a, { reason: 'The call could not be connected.', unclean: a.inviteStarted }, false);
      void this._stopNative(a);
    }, TelnyxVoiceManager.CONNECT_TIMEOUT_MS);
    clientLogger.info('[TelnyxVoice] attempt started', a.id);
    void this._refreshAudioDevices(a); // read-only discovery also offers idle Bluetooth during login
    try {
      const permission = await CapacitorTelnyxVoice.checkMicrophonePermission();
      if (!this._canDial(a)) return;
      if (!permission.granted) {
        const requested = await CapacitorTelnyxVoice.requestMicrophonePermission();
        if (!this._canDial(a)) return;
        if (!requested.granted) { this._complete(a, { reason: 'Microphone permission denied.' }); return; }
      }
      await this._nativeStopBarrier;
      await this._cleanupBarrier;
      if (!this._canDial(a)) return;
      if (!this._initialized) await this.init();
      await this._nativeStopBarrier;
      await this._cleanupBarrier;
      if (!this._canDial(a)) return;
      void this._refreshAudioDevices(a);
      await this._nativeMakeCall(a, { to, callerId: params.callerIdE164, displayName: params.displayName,
        clientState: this._buildClientState(params.jobId, params.callerIdE164), attemptId: a.id });
      if (!this._live(a)) return;
      a.nativeReady = true;
      await this._flushAudioRoute(a);
    } catch (err) {
      if (!this._live(a)) return;
      this._initialized = false;
      this._complete(a, { reason: this._startErrorMessage(err), unclean: a.inviteStarted }, false);
      if (a.inviteStarted) void this._stopNative(a);
      // The ended overlay owns this failure; do not leak raw SDK/provider errors into caller toasts.
    }
  }

  private async _nativeMakeCall(a: Attempt, opts: NativeCallOptions): Promise<void> {
    if (!this._canDial(a)) return;
    a.inviteStarted = true;
    try { await CapacitorTelnyxVoice.makeCall(opts); }
    catch (err) {
      const error = err as { code?: string; message?: string };
      const retryable = error?.code === 'CLIENT_SPENT' || error?.code === 'SOCKET_DISCONNECTED'
        || /not logged in/i.test(error?.message ?? '');
      if (retryable || error?.code === 'CALL_IN_PROGRESS' || /call is already in progress/i.test(error?.message ?? '')) {
        a.inviteStarted = false; // Native rejected before INVITE; don't hang up an unrelated call.
      }
      if (!retryable) throw err;
      if (!this._canDial(a)) return;
      this._initialized = false;
      await this.init();
      if (!this._canDial(a)) return;
      await this._cleanupBarrier;
      if (!this._canDial(a)) return;
      a.inviteStarted = true;
      try { await CapacitorTelnyxVoice.makeCall(opts); }
      catch (second) {
        const code = (second as { code?: string })?.code;
        if (code === 'CLIENT_SPENT' || code === 'SOCKET_DISCONNECTED' || code === 'CALL_IN_PROGRESS') a.inviteStarted = false;
        throw second;
      }
    }
  }

  async endCall(callSid?: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const a = this._attempt;
    if (!a || (callSid && a.callSid && callSid.toLowerCase() !== a.callSid.toLowerCase())) return;
    a.cancelled = true;
    a.retryPending = false;
    this._clearTimers(a);
    if (a.terminal) { this._emit({ state: 'idle' }); return; }
    this._emit({ ...this._currentState, state: 'ended', error: undefined, canRedial: false, isMuted: false });
    this._scheduleIdle(a);
    // Return promptly. Native termination and server cleanup retain their own barriers/identity.
    void this._stopNative(a);
  }

  private _stopNative(a: Attempt): Promise<void> {
    if (a.stop) return a.stop;
    // Install the barrier BEFORE invoking native: some implementations emit their event inline.
    let release!: () => void;
    const stop = new Promise<void>((resolve) => { release = resolve; });
    a.stop = stop;
    this._nativeStopBarrier = stop;
    void (async () => {
      if (a.inviteStarted) {
        try { await this._within(CapacitorTelnyxVoice.endCall({ callSid: a.callSid, attemptId: a.id }), 3_000); }
        catch (err) { clientLogger.debug('[TelnyxVoice] native end incomplete', a.id, err); }
      }
      // Some older native builds resolve endCall without delivering a terminal event.
      if (!a.terminal) this._complete(a, { unclean: a.inviteStarted, userInitiated: a.cancelled }, false);
    })().finally(release);
    return stop;
  }

  private _complete(a: Attempt, event: NativeEvent, allowRetry = true): void {
    if (a.terminal) return;
    a.terminal = true;
    a.cancelled ||= event.userInitiated === true;
    this._clearTimers(a);
    const unclean = event.unclean === true || (a.cancelled && (!a.answered || a.recovering) && a.inviteStarted);
    const cleanup = unclean ? this._serverHangupFallback(a) : Promise.resolve(true);
    if (this._attempt !== a) return;
    this._initialized = false;
    const canRedial = !a.cancelled && (unclean || !!event.reason);
    a.retryPending = allowRetry && unclean && !a.cancelled && !a.answered && a.retryCount < 1;
    this._emit({ ...this._currentState, state: 'ended', isMuted: false, canRedial,
      error: a.cancelled ? undefined : event.reason ? this._startErrorMessage(event.reason)
        : unclean ? 'Call dropped — connection lost.' : undefined });
    this._scheduleIdle(a, canRedial ? TelnyxVoiceManager.REDIAL_ENDED_MS : 2_000);
    clientLogger.info('[TelnyxVoice] attempt ended', a.id, { unclean, cancelled: a.cancelled });
    if (a.retryPending) {
      void cleanup.then((ok) => {
        if (this._attempt !== a || a.cancelled || !a.retryPending) return;
        if (!ok || !this._sameContext(a)) {
          a.retryPending = false;
          this._scheduleIdle(a, TelnyxVoiceManager.REDIAL_ENDED_MS);
          this._reconnectForNextCall();
          return;
        }
        if (a.idleTimer) clearTimeout(a.idleTimer);
        // A retry wait still has a usable Cancel button. It isn't a new call attempt yet.
        this._emit({ ...this._currentState, state: 'connecting', error: undefined, canRedial: false });
        a.redialTimer = setTimeout(() => {
          if (this._attempt === a && !a.cancelled && a.retryPending && this._sameContext(a)) {
            a.retryPending = false;
            void this.redial(false);
          }
        }, TelnyxVoiceManager.REDIAL_DELAY_MS);
      });
    } else { this._reconnectForNextCall(); }
  }

  private _serverHangupFallback(a: Attempt): Promise<boolean> {
    if (a.cleanup) return a.cleanup;
    // This API targets the latest active outbound call, not an SDK call UUID. Never replay it
    // after a newer INVITE or in a different tenant context.
    if (!a.inviteStarted || !a.orgId || !a.companyId || !this._sameContext(a)
      || (this._attempt !== a && this._attempt?.inviteStarted)) {
      return a.cleanup = Promise.resolve(false);
    }
    const cleanup = apiService.hangupActiveVoiceCall({ orgId: a.orgId, companyId: a.companyId }).then(
      (result) => result.hungUp || result.reason === 'no active outbound call',
      (err) => { clientLogger.debug('[TelnyxVoice] server cleanup failed', a.id, err); return false; },
    );
    a.cleanup = cleanup;
    this._cleanupBarrier = Promise.all([this._cleanupBarrier, cleanup]).then(() => undefined);
    return cleanup;
  }

  private _clearTimers(a: Attempt): void {
    if (a.watchdog) clearTimeout(a.watchdog);
    if (a.redialTimer) clearTimeout(a.redialTimer);
    if (a.idleTimer) clearTimeout(a.idleTimer);
    a.watchdog = a.redialTimer = a.idleTimer = undefined;
  }
  private _scheduleIdle(a: Attempt, delay = 2_000): void {
    if (a.idleTimer) clearTimeout(a.idleTimer);
    a.idleTimer = setTimeout(() => {
      if (this._attempt === a && this._currentState.state === 'ended' && !a.retryPending) this._emit({ state: 'idle' });
    }, delay);
  }

  async muteCall(muted: boolean, callSid?: string): Promise<void> {
    const a = this._attempt;
    if (!Capacitor.isNativePlatform() || !this._live(a)) return;
    await CapacitorTelnyxVoice.muteCall({ muted, callSid });
    if (this._live(a)) this._emit({ ...this._currentState, isMuted: muted });
  }
  async getAudioDevices(): Promise<{ devices: AudioDevice[]; selected: AudioDeviceType; callAudioActive?: boolean }> {
    if (!Capacitor.isNativePlatform()) return { devices: [{ type: 'earpiece', name: 'Phone' }], selected: 'earpiece' };
    return CapacitorTelnyxVoice.getAudioDevices();
  }
  async selectAudioDevice(type: AudioDeviceType): Promise<void> {
    const a = this._attempt;
    if (!Capacitor.isNativePlatform() || !this._live(a)) return;
    a.requestedAudioRoute = type;
    a.routeRequestId++;
    this._emit({ ...this._currentState, audioRouteError: undefined });
    await this._flushAudioRoute(a);
  }
  private async _flushAudioRoute(a: Attempt): Promise<void> {
    if (!this._live(a) || !a.nativeReady) return; // keep the choice through permission/login/INVITE
    if (a.routeCommand) return a.routeCommand;
    const run = async () => {
      while (this._live(a) && a.requestedAudioRoute && a.sentRouteRequestId < a.routeRequestId) {
        const requestId = a.routeRequestId;
        const type = a.requestedAudioRoute;
        a.sentRouteRequestId = requestId;
        try {
          await CapacitorTelnyxVoice.selectAudioDevice({ type, attemptId: a.id, callSid: a.callSid,
            routeRequestId: requestId });
          if (this._live(a) && requestId === a.routeRequestId) await this._refreshAudioDevices(a);
        } catch {
          // Audio selection failure never terminates a connected call or exposes native error text.
          if (this._live(a) && requestId === a.routeRequestId) {
            this._emit({ ...this._currentState,
              audioRouteError: "Couldn't switch call audio. Choose another audio device or try again." });
          }
        }
      }
    };
    // Defer invocation until the in-flight marker is installed (native mocks may complete inline).
    a.routeCommand = Promise.resolve().then(run);
    try { await a.routeCommand; }
    finally { a.routeCommand = undefined; }
  }
  async sendDigits(digits: string): Promise<void> {
    if (Capacitor.isNativePlatform() && this._callActive) await CapacitorTelnyxVoice.sendDigits({ digits });
  }
  get currentState(): VoiceCallEvent { return { ...this._currentState }; }
  private _emit(event: VoiceCallEvent): void {
    this._currentState = event;
    this._listeners.forEach((listener) => listener({ ...event }));
  }
  private async _refreshAudioDevices(a: Attempt): Promise<void> {
    const snapshotId = ++a.audioSnapshotId;
    try {
      const { devices, selected, callAudioActive } = await this.getAudioDevices();
      if (this._live(a) && snapshotId === a.audioSnapshotId) {
        // iOS can report idle media speaker/A2DP while native call audio is still gated off.
        // Continue offering those devices without presenting the idle output as this call's route.
        this._emit({ ...this._currentState, availableAudioDevices: devices,
          audioRoute: callAudioActive === false ? this._currentState.audioRoute : selected });
      }
    } catch (err) { clientLogger.debug('[TelnyxVoice] audio device lookup failed:', err); }
  }

  private _eventAttempt(data: NativeEvent): Attempt | undefined {
    let a: Attempt | undefined;
    if (data.attemptId) a = this._attempts.get(data.attemptId);
    else if (data.callSid) a = [...this._attempts.values()].find((item) => item.callSid?.toLowerCase() === data.callSid?.toLowerCase());
    if (!a && !data.attemptId && this._attempt?.inviteStarted && !this._attempt.terminal) a = this._attempt;
    if (!a || !a.inviteStarted) return undefined;
    if (data.callSid && a.callSid && data.callSid.toLowerCase() !== a.callSid.toLowerCase()) return undefined;
    if (data.callSid) a.callSid = data.callSid;
    return a;
  }
  private async _registerListeners(): Promise<void> {
    for (const [name, state] of [['callRinging', 'ringing'], ['callConnected', 'connected'], ['callReconnecting', 'reconnecting']] as const) {
      await CapacitorTelnyxVoice.addListener(name, (data) => {
        const a = this._eventAttempt(data);
        if (!a || !this._live(a)) return;
        if (state === 'connected') {
          a.answered = true; a.recovering = false;
          if (a.watchdog) clearTimeout(a.watchdog);
          a.watchdog = undefined;
        } else if (state === 'reconnecting') a.recovering = true;
        else { if (a.answered) return; a.recovering = false; }
        this._emit({ ...this._currentState, state, callSid: a.callSid });
        if (state !== 'reconnecting') void this._refreshAudioDevices(a);
      });
    }
    await CapacitorTelnyxVoice.addListener('audioDevicesChanged', (data) => {
      const a = data.attemptId || data.callSid ? this._eventAttempt(data) : this._attempt;
      if (!a || !this._live(a)) return;
      if (data.routeRequestId != null && data.routeRequestId < a.routeRequestId) return;
      ++a.audioSnapshotId; // an older enumeration response must not overwrite this native observation
      this._emit({ ...this._currentState, availableAudioDevices: data.devices ?? [],
        audioRoute: (data.callAudioActive === false ? this._currentState.audioRoute : data.selected)
          ?? this._currentState.audioRoute ?? 'earpiece',
        audioRouteError: data.routeError === undefined ? this._currentState.audioRouteError
          : data.routeError ? "Couldn't switch call audio. Choose another audio device or try again." : undefined });
    });
    for (const name of ['callDisconnected', 'callError']) {
      await CapacitorTelnyxVoice.addListener(name, (data) => {
        const a = this._eventAttempt(data);
        if (a) this._complete(a, data);
        // Login errors already reject login(). Never recursively re-login from idle callError.
        else if (name === 'callError' && !this._callActive) this._initialized = false;
      });
    }
  }

  private _startErrorMessage(err: unknown): string {
    const detail = typeof err === 'string' ? err : (err as { message?: string })?.message ?? '';
    const code = detail.match(/-3200\d/)?.[0];
    if (code === '-32000' || code === '-32001') return `Your phone session expired. Please try again. (${code})`;
    if (code) return `Couldn't reach the phone service. Check your connection and try again. (${code})`;
    // Only our own plain-language messages are displayed; SDK errors can expose provider names.
    if (/^(The call |Call dropped|Microphone permission|Your organization)/.test(detail) && !/telnyx/i.test(detail)) return detail;
    return "Couldn't start the call. Check your connection and try again.";
  }

  private _buildClientState(jobId: string | undefined, callerId: string): string {
    const parts = this._identity.split('_');
    const state = { orgId: parts[0] ?? '', companyId: apiService.getCompanyId() ?? parts[1] ?? '',
      userId: parts[2] ?? '', jobId: jobId ?? (parts.length === 4 ? parts[3] : undefined), callerId };
    return btoa(JSON.stringify(state)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  private _decodeJwtExpMs(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const exp = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).exp;
      return typeof exp === 'number' ? exp * 1000 : null;
    } catch { return null; }
  }
  private async _fetchTokenAndLogin(generation: number): Promise<void> {
    const orgId = apiService.getOrgId(), companyId = apiService.getCompanyId();
    const current = () => generation === this._sessionGeneration
      && orgId === apiService.getOrgId() && companyId === apiService.getCompanyId();
    const now = Date.now();
    if (!this._token || now >= this._tokenExpMs) {
      const tok = await this._within(apiService.getVoiceToken(), 20_000);
      if (!current()) throw new Error('Phone session changed.');
      this._token = tok.token;
      this._identity = tok.identity;
      const serverExpMs = now + Math.max((tok.ttl - 60) * 1000, 30_000);
      const jwtExpMs = this._decodeJwtExpMs(tok.token);
      this._tokenExpMs = jwtExpMs ? Math.min(serverExpMs, jwtExpMs - 60_000) : serverExpMs;
      this._scheduleRefresh(this._tokenExpMs - now);
    }
    if (!current()) throw new Error('Phone session changed.');
    // Native 20s timeout is primary; this bound also releases callers using an older iOS binary.
    await this._within(CapacitorTelnyxVoice.login({ token: this._token }), 25_000);
    if (!current()) throw new Error('Phone session changed.');
  }
  private _scheduleRefresh(delayMs: number): void {
    this._clearRefreshTimer();
    this._refreshTimer = setTimeout(() => {
      this._token = ''; this._tokenExpMs = 0;
      if (!this._callActive) this._reconnectForNextCall();
    }, Math.max(delayMs, 1_000));
  }
  private _clearRefreshTimer(): void {
    if (this._refreshTimer !== null) clearTimeout(this._refreshTimer);
    this._refreshTimer = null;
  }
  private _within<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('Phone operation timed out.')), ms);
      promise.then((value) => { clearTimeout(timer); resolve(value); }, (err) => { clearTimeout(timer); reject(err); });
    });
  }
}
export const telnyxVoiceManager = TelnyxVoiceManager.instance;
